import {
  AlertTriangle,
  CheckCircle2,
  CloudCog,
  Mail as MailIcon,
  Workflow,
  type LucideIcon,
} from "lucide-react";

type Scenario = {
  tag: string;
  TagIcon: LucideIcon;
  petal: string;
  tint: string;
  headline: string;
  situation: string;
  approach: string;
  outcome: string;
};

const SCENARIOS: Scenario[] = [
  {
    tag: "Aging line-of-business app",
    TagIcon: AlertTriangle,
    petal: "var(--petal-coral)",
    tint: "rgba(229,69,62,.10)",
    headline: `"Our Access database is one crash away from a very bad week."`,
    situation:
      "An Access front-end built in 2009 runs your whole back office. The original developer is gone, the file lives on a shared drive, and nobody wants to touch it.",
    approach:
      "Lift the data into Postgres or SQL Server first — same shape, same screens, just a real database underneath. Then rebuild the UI one form at a time. The old app keeps running until each piece is proven.",
    outcome: "You stop losing sleep before the rewrite is finished.",
  },
  {
    tag: "Glue between systems",
    TagIcon: Workflow,
    petal: "var(--petal-leaf)",
    tint: "rgba(91,199,66,.12)",
    headline: `"Three people spend Monday morning copy-pasting CSVs."`,
    situation:
      "QuickBooks holds the invoices, Shopify holds the orders, and a shared spreadsheet pretends to hold both. The reconciliation is a person, not a process.",
    approach:
      "A small scheduled service that pulls from both APIs nightly, reconciles in a staging table, writes a clean report, and flags real mismatches. No new SaaS to buy, no new dashboards to learn.",
    outcome: "Monday morning gets twelve hours of its life back.",
  },
  {
    tag: "Production mail & postage",
    TagIcon: MailIcon,
    petal: "var(--petal-sun)",
    tint: "rgba(245,179,36,.14)",
    headline: `"Our mail vendor is fine. We just don't know if we're leaving money on the table."`,
    situation:
      "You run monthly mailings — statements, notices, marketing — through a vendor. The bills are predictable, but nobody on the team can read a presort report or tell you what postal tier you're hitting.",
    approach:
      "Read your last few jobs end to end. Check presort qualification, address quality, IMb usage, commingling opportunities, and vendor pricing. Write you a plain-English report with the dollar impact of each lever.",
    outcome: "You walk into the next vendor call knowing what to ask.",
  },
  {
    tag: "On-prem to cloud",
    TagIcon: CloudCog,
    petal: "var(--petal-sky)",
    tint: "rgba(0,166,223,.12)",
    headline: `"The server in the closet is older than the youngest person who uses it."`,
    situation:
      "One physical box runs your file shares, an internal app, and the backup script that nobody has tested. Your IT shop wants out, and your vendor wants to sell you a $50k cloud migration.",
    approach:
      "Honest assessment first: what actually needs to move, what should stay, what should die. Then a staged path — usually Azure or AWS, sometimes a small managed VPS — sized to the workload, not the sales deck.",
    outcome: "The closet goes quiet. The backups actually run.",
  },
];

export function Scenarios() {
  return (
    <section className="section scenarios" id="scenarios">
      <div className="container">
        <div className="section-head reveal">
          <span className="eyebrow">Scenarios we'd take on</span>
          <h2>
            What it could
            <br />
            <em>look like.</em>
          </h2>
          <p>
            We're a new shop. Real client stories will land here as they come in.
            Until then, these are the shapes of problems we know well — and how
            we'd walk into each one.
          </p>
        </div>

        <div className="scenarios-grid">
          {SCENARIOS.map((s) => (
            <article className="scenario-card reveal" key={s.tag}>
              <span className="stripe" style={{ background: s.petal }} />
              <div
                className="scenario-tag"
                style={{ background: s.tint, color: s.petal }}
              >
                <s.TagIcon size={14} />
                {s.tag}
              </div>
              <h3>{s.headline}</h3>
              <div className="scenario-block">
                <div className="scenario-label">The situation</div>
                <p>{s.situation}</p>
              </div>
              <div className="scenario-block">
                <div className="scenario-label">What we'd do</div>
                <p>{s.approach}</p>
              </div>
              <div className="scenario-footer">
                <CheckCircle2 size={14} style={{ color: "var(--petal-leaf)" }} />
                {s.outcome}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
